import "./styles/main.css";
import type { Locale } from "./types";
import { renderApp } from "./render";
import {
  loadContent,
  persistLocale,
  resolveInitialLocale,
  SUPPORTED_LOCALES,
} from "./content";
import {
  initBackToTop,
  initContactForm,
  initLangSwitch,
  initNav,
  initNewsletter,
  initReveal,
  initScrollSpy,
  initThemeToggle,
  type Cleanup,
} from "./interactions";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("找不到 #app 挂载点");

// 标记 JS 就绪：入场动画的初始隐藏态只在 .js 下生效，
// 无脚本时内容照常可见（利于 SEO 与渐进增强）。
document.documentElement.classList.add("js");

let cleanups: Cleanup[] = [];

/** 解绑所有交互监听器（重新挂载 / 语言切换 / HMR dispose 前调用） */
function teardown(): void {
  cleanups.forEach((fn) => fn());
  cleanups = [];
}

async function mount(locale: Locale): Promise<void> {
  teardown();

  const content = await loadContent(locale);

  document.title = content.meta.title;
  document.documentElement.lang = content.meta.lang;

  app!.innerHTML = renderApp(content, locale, SUPPORTED_LOCALES);

  // 渲染后绑定交互，收集各自的清理函数
  cleanups.push(
    initNav(app!),
    initReveal(app!),
    initScrollSpy(app!),
    initBackToTop(app!),
    initContactForm(app!, content.contact.form),
    initNewsletter(app!, content.newsletter),
    initLangSwitch(app!, switchLocale),
    initThemeToggle(app!),
  );
}

function switchLocale(locale: Locale): void {
  persistLocale(locale);
  void mount(locale);
}

void mount(resolveInitialLocale());

// ---- HMR ----
// 建立热更新边界：编辑内容 / 组件 / 样式时，Vite 会热替换本模块并重跑
// mount()，而不是整页刷新；dispose 里先解绑监听器，避免热重载累积泄漏。
// 语言选择已持久化到 localStorage，故热更新后仍保留当前语言。
if (import.meta.hot) {
  import.meta.hot.dispose(teardown);
  import.meta.hot.accept();
}
